// Cookie utilities for secure token storage
export const CookieStorage = {
  // Set a cookie with security options
  setCookie(name, value, options = {}) {
    const defaults = {
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'Strict',
      expires: null // Session cookie by default
    };

    const settings = { ...defaults, ...options };

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (settings.expires) {
      cookieString += `; expires=${settings.expires.toUTCString()}`;
    }

    if (settings.maxAge) {
      cookieString += `; max-age=${settings.maxAge}`;
    }

    cookieString += `; path=${settings.path}`;

    if (settings.secure) {
      cookieString += `; secure`;
    }

    cookieString += `; samesite=${settings.sameSite}`;

    document.cookie = cookieString;
  },

  // Get a cookie value
  getCookie(name) {
    const nameEQ = encodeURIComponent(name) + "=";
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      let c = cookie.trim();
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }
    return null;
  },

  // Remove a cookie
  removeCookie(name, options = {}) {
    const defaults = {
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'Strict'
    };

    const settings = { ...defaults, ...options };

    let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    cookieString += `; path=${settings.path}`;

    if (settings.secure) {
      cookieString += `; secure`;
    }

    cookieString += `; samesite=${settings.sameSite}`;

    document.cookie = cookieString;
  },

  // Check if a cookie exists
  hasCookie(name) {
    return this.getCookie(name) !== null;
  }
};

// Token-specific storage using cookies
export const TokenStorage = {
  // Set access token as secure cookie
  setAccessToken(token, expiresIn = 3600) {
    const expirationDate = new Date();
    expirationDate.setTime(expirationDate.getTime() + (expiresIn * 1000));

    CookieStorage.setCookie('access_token', token, {
      expires: expirationDate,
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax'
    });

    // Store expiration time separately for validation
    CookieStorage.setCookie('token_expires_at', expirationDate.getTime().toString(), {
      expires: expirationDate,
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax'
    });

    return true;
  },

  // Get access token if valid
  getAccessToken() {
    const token = CookieStorage.getCookie('access_token');
    const expiresAt = CookieStorage.getCookie('token_expires_at');

    if (token && expiresAt) {
      const expirationTime = parseInt(expiresAt, 10);
      if (Date.now() < expirationTime) {
        return token;
      } else {
        // Token expired, remove it
        this.removeAccessToken();
      }
    }
    return null;
  },

  // Check if token exists and is valid
  isTokenValid() {
    const token = CookieStorage.getCookie('access_token');
    const expiresAt = CookieStorage.getCookie('token_expires_at');

    if (token && expiresAt) {
      const expirationTime = parseInt(expiresAt, 10);
      return Date.now() < expirationTime;
    }
    return false;
  },

  // Get token expiration time
  getTokenExpiration() {
    const expiresAt = CookieStorage.getCookie('token_expires_at');
    return expiresAt ? parseInt(expiresAt, 10) : null;
  },

  // Remove access token
  removeAccessToken() {
    CookieStorage.removeCookie('access_token');
    CookieStorage.removeCookie('token_expires_at');
    return true;
  },

  // Set refresh token (longer expiration)
  setRefreshToken(token, expiresIn = 86400 * 7) { // 7 days default
    const expirationDate = new Date();
    expirationDate.setTime(expirationDate.getTime() + (expiresIn * 1000));

    CookieStorage.setCookie('refresh_token', token, {
      expires: expirationDate,
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax'
    });

    return true;
  },

  // Get refresh token
  getRefreshToken() {
    return CookieStorage.getCookie('refresh_token');
  },

  // Remove refresh token
  removeRefreshToken() {
    CookieStorage.removeCookie('refresh_token');
    return true;
  },

  // Clear all tokens
  clearAllTokens() {
    this.removeAccessToken();
    this.removeRefreshToken();
  }
};

// User data storage (still using sessionStorage for non-sensitive data)
export const UserStorage = {
  setUserDetails(userDetails) {
    try {
      sessionStorage.setItem('user_details', JSON.stringify(userDetails));
      return true;
    } catch (error) {
      console.error('Failed to store user details:', error);
      return false;
    }
  },

  getUserDetails() {
    try {
      const userDetails = sessionStorage.getItem('user_details');
      return userDetails ? JSON.parse(userDetails) : null;
    } catch (error) {
      console.error('Failed to retrieve user details:', error);
      return null;
    }
  },

  removeUserDetails() {
    try {
      sessionStorage.removeItem('user_details');
      return true;
    } catch (error) {
      console.error('Failed to remove user details:', error);
      return false;
    }
  }
};

export default { CookieStorage, TokenStorage, UserStorage };